import {
  UploadDtoStatusEnum,
  UploadFailedDataDto,
  UploadScheduledDataDto,
} from '@app/modules/social-export/social-export.types';
import { QueryView } from '@mi-front/ui';
import { Alert, Box, CircularProgress } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useIntl } from 'react-intl';
import { useSocialUpload } from './hooks/useSocialUpload';
import { SocialUploadCompletedStatus } from './SocialUploadCompletedStatus';
import { SocialUploadScheduledStatus } from './SocialUploadScheduledStatus';

type SocialUploadStatusParams = { id: string } | { id: { platform: string; key: string } };

export function SocialUploadStatus({
  exportAction,
  renderNoUpload,
  ...params
}: SocialUploadStatusParams & {
  exportAction?: React.ReactNode;
  renderNoUpload?: () => React.ReactNode;
}) {
  const { $t } = useIntl();
  const uploadQuery = useSocialUpload(params);

  return (
    <QueryView query={uploadQuery} inline ignore404>
      {(upload) => {
        if (!upload) {
          return <>{renderNoUpload?.()}</>;
        }

        if (upload.data.status === UploadDtoStatusEnum.COMPLETED) {
          return <SocialUploadCompletedStatus upload={upload} />;
        }

        if (upload.data.status === UploadDtoStatusEnum.SCHEDULED) {
          const scheduledData = upload.data as UploadScheduledDataDto;
          return <SocialUploadScheduledStatus upload={upload} scheduledData={scheduledData} />;
        }

        if (
          upload.data.status === UploadDtoStatusEnum.WAITING ||
          upload.data.status === UploadDtoStatusEnum.IN_PROGRESS
        ) {
          return (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              <Box display="flex" alignItems="center" gap={1}>
                <AlertTitle>
                  {$t({
                    id: 'fragments.export.publishingInProgress',
                    defaultMessage: 'Publicación en curso...',
                  })}
                </AlertTitle>
              </Box>
            </Alert>
          );
        }

        if (upload.data.status === UploadDtoStatusEnum.FAILED) {
          const failedData = upload.data as UploadFailedDataDto;
          return (
            <Alert severity="error">
              <AlertTitle>
                {$t({
                  id: 'fragments.export.publishError',
                  defaultMessage: 'Ocurrió un error al publicar',
                })}
              </AlertTitle>
              <Box mt={2}>
                {$t({
                  id: 'common.error',
                  defaultMessage: 'Error',
                })}{' '}
                <i>{failedData.error}</i>
              </Box>
              {exportAction && <Box mt={2}>{exportAction}</Box>}
            </Alert>
          );
        }

        return <Box />;
      }}
    </QueryView>
  );
}
